import React, { useState } from "react";
import { register } from "../../firebase";

interface EmailRegisterProps {
  onRegisterSuccess?: () => void;
  onBackToLogin?: () => void;
}

const EmailRegister: React.FC<EmailRegisterProps> = ({
  onRegisterSuccess,
  onBackToLogin,
}) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setAlert({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setAlert({
        open: true,
        message: "Passwords do not match",
        severity: "error",
      });
      return false;
    }

    if (formData.password.length < 6) {
      setAlert({
        open: true,
        message: "Password must be at least 6 characters long",
        severity: "error",
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setAlert({
        open: true,
        message: "Please enter a valid email address",
        severity: "error",
      });
      return false;
    }

    if (!acceptedTerms) {
      setAlert({
        open: true,
        message: "Please accept the Terms and Conditions to continue",
        severity: "error",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Create user profile data
      const userData = {
        email: formData.email,
        password: formData.password,
      };

      await register(formData.email, formData.password, userData);

      setAlert({
        open: true,
        message: "Account created successfully! You are now signed in.",
        severity: "success",
      });

      setTimeout(() => {
        window.location.href = "/portal/dashboard";
      }, 1500);
    } catch (error: any) {
      let errorMessage = "Failed to create account. Please try again.";

      if (error.code === "auth/email-already-in-use") {
        errorMessage =
          "An account with this email already exists. Try signing in instead.";
      } else if (error.code === "auth/weak-password") {
        errorMessage =
          "Password is too weak. Please choose a stronger password.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      }

      setAlert({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <div className="p-6 w-full max-w-md mx-auto bg-white rounded-lg shadow-md">
      <h2 className="text-base font-semibold leading-7 text-gold text-center">
        Athlete Portal
      </h2>
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
        Create Account
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <div className="mb-4">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email Address *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold"
            required
            autoComplete="email"
          />
        </div>

        {/* Password */}
        <div className="mb-4">
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Password *
          </label>
          <input
            id="password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold"
            required
            minLength={6}
            autoComplete="new-password"
          />
          <p className="text-xs text-gray-500 mt-1">
            Must be at least 6 characters
          </p>
        </div>

        {/* Confirm Password */}
        <div className="mb-4">
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Confirm Password *
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold"
            required
            minLength={6}
            autoComplete="new-password"
          />
        </div>

        {/* Terms and Conditions */}
        <div className="mb-4">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="w-4 h-4 text-gold bg-gray-100 border-gray-300 rounded focus:ring-gold focus:ring-2 mt-1 mr-3 flex-shrink-0"
            />
            <span className="text-sm text-gray-700">
              I agree to the{" "}
              <button
                type="button"
                onClick={() => setShowTermsDialog(true)}
                className="text-gold hover:text-gold underline focus:outline-none"
              >
                Terms and Conditions
              </button>{" "}
              *
            </span>
          </label>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !acceptedTerms}
          className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gold hover:bg-gold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold ${
            loading || !acceptedTerms ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Creating Account...
            </>
          ) : (
            "Create Account"
          )}
        </button>

        {/* Back to Login */}
        <div className="text-center">
          <button
            type="button"
            onClick={onBackToLogin}
            className="text-sm text-gray-500 hover:text-gray-500 focus:outline-none focus:underline"
          >
            Already have an account? Sign in
          </button>
        </div>
      </form>

      {alert.open && (
        <div
          className={`mt-4 p-4 rounded-md ${
            alert.severity === "error"
              ? "bg-red-50 border border-red-200 text-red-700"
              : "bg-green-50 border border-green-200 text-green-700"
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="text-sm">{alert.message}</span>
            <button onClick={handleCloseAlert} className="ml-4">
              <svg
                className={`h-5 w-5 ${
                  alert.severity === "error" ? "text-red-500" : "text-green-500"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Terms and Conditions Dialog */}
      {showTermsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Athlete Sponsorship Agreement Terms and Conditions
              </h3>
              <button
                onClick={() => setShowTermsDialog(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose prose-sm max-w-none">
                <h4 className="text-base font-semibold mb-3">
                  1. Acceptance of Terms
                </h4>
                <p className="mb-4 text-sm text-gray-700">
                  By creating an account with WattleNest, you agree to be bound
                  by these Terms and Conditions for. If you do not agree to
                  these terms, please do not use our services.
                </p>

                <h4 className="text-base font-semibold mb-3">2. Purpose</h4>
                <p className="mb-4 text-sm text-gray-700">
                  This Agreement outlines the terms and conditions of
                  sponsorship provided by WattleNest to the Athlete to support
                  their sporting campaign, ensuring responsible use of funds and
                  fostering a culture of giving back to the wider sporting
                  community.
                </p>

                <h4 className="text-base font-semibold mb-3">3. Term</h4>
                <p className="mb-4 text-sm text-gray-700">
                  This Agreement shall commence from the date an athlete
                  application submitted online via the athlete portal is
                  approved from WattleNest and will continue until an agreed end
                  date unless terminated earlier as per Clause 7.
                </p>

                <h4 className="text-base font-semibold mb-3">
                  4. Sponsorship Support
                </h4>
                <p className="mb-4 text-sm text-gray-700">
                  WattleNest agrees to provide financial support to the Athlete
                  for the purpose of funding training, competition, travel, and
                  other related expenses essential to their sporting campaign as
                  and where funds become available. The terms and conditions in
                  this Agreement are valid once WattleNest has made a payment to
                  the Athlete of any value in any given year. These terms
                  replace any previous terms and conditions that may have been
                  mutually agreed.
                </p>

                <h4 className="text-base font-semibold mb-3">
                  5. Obligatin of the Athlete
                </h4>
                <p className="mb-4 text-sm text-gray-700">
                  The Athlete agrees to: <br></br>a) Use the funds exclusively
                  for expenses related to their sporting campaign, including but
                  not limited to training, competition fees, equipment, travel,
                  and coaching.<br></br> b) Represent WattleNest in a
                  professional and ethical manner in all public and media
                  appearances.<br></br> c) Acknowledge WattleNest as a sponsor
                  in social media, interviews, and promotional materials as
                  agreed upon. <br></br>d) Participate in a minimum of two (2)
                  promotional events or community engagements annually on behalf
                  of WattleNest and actively promote, support, and endorse the
                  WattleNest brand.
                </p>

                <h4 className="text-base font-semibold mb-3">6. Giving Back</h4>
                <p className="mb-4 text-sm text-gray-700">
                  The Athlete is expected to give back to the WattleNest
                  community by supporting fellow athletes, participating in
                  community and charitable initiatives aligned with
                  WattleNest&#39;s mission, and engaging with organisations that
                  promote stronger, healthier communities. This may include
                  mentoring, event participation, awareness campaigns, or
                  advocacy, as and where possible. WattleNest encourages a
                  culture of contribution and reciprocity in the spirit of
                  building a sustainable future for Australian athletes.
                </p>

                <h4 className="text-base font-semibold mb-3">
                  7. Participation & Representation
                </h4>
                <p className="mb-4 text-sm text-gray-700">
                  The Athlete agrees to:
                  <br></br>a) Regularly collaborate with WattleNest and its
                  partners through social media, including sharing relevant
                  updates, tagging WattleNest, and engaging in key campaigns.
                  <br></br>b) Publicly thank and acknowledge WattleNest in media
                  releases, public statements, and press coverage relating to
                  their sporting achievements.
                  <br></br>c) Represent WattleNest with compassion,
                  professionalism, and dignity at all times.
                  <br></br>d) Seek opportunities, where appropriate, to advocate
                  for or support the values and vision of WattleNest through
                  personal and professional platforms.
                </p>

                <h4 className="text-base font-semibold mb-3">
                  8. Reporting & Transparency
                </h4>
                <p className="mb-4 text-sm text-gray-700">
                  The Athlete shall provide WattleNest with: <br></br>a) A
                  bi-annual report detailing the use of funds and progress of
                  their sporting campaign in any year funding from the
                  WattleNest has been provided <br></br>b) Receipts or evidence
                  of expenditures upon reasonable request. <br></br>c)
                  Notification of any significant changes to their sporting
                  status, sponsorship arrangements, or public profile that may
                  impact their role as a WattleNest ambassador.
                </p>

                <h4 className="text-base font-semibold mb-3">9. Termination</h4>
                <p className="mb-4 text-sm text-gray-700">
                  This Agreement may be terminated: <br></br>a) By mutual
                  agreement of both parties. <br></br>b) By WattleNest if the
                  Athlete is found to have misused sponsorship funds or engaged
                  in conduct that damages WattleNest’s reputation. <br></br>c)
                  By the Athlete with 30 days&#39; written notice if they no
                  longer wish to participate in the sponsorship program.
                </p>

                <h4 className="text-base font-semibold mb-3">
                  10. Confidentiality
                </h4>
                <p className="mb-4 text-sm text-gray-700">
                  Both parties agree to keep confidential any financial details
                  and personal information exchanged under this Agreement,
                  except where disclosure is required by law.
                </p>

                <h4 className="text-base font-semibold mb-3">
                  11. General Provisions
                </h4>
                <p className="mb-4 text-sm text-gray-700">
                  a) This Agreement does not create an employment relationship
                  between the parties. <br></br>b) Any disputes arising under
                  this Agreement will be resolved through good faith
                  negotiations and, if necessary, mediation. <br></br>c)
                  Amendments to this Agreement must be in writing and signed by
                  both parties.
                </p>

                <h4 className="text-base font-semibold mb-3">
                  11. Acceptance & Digital Consent
                </h4>
                <p className="mb-4 text-sm text-gray-700">
                  By submitting an online application and creating a profile
                  through the WattleNest platform, the Athlete acknowledges that
                  they have read, understood, and agree to be bound by the terms
                  and conditions outlined in this Sponsorship Agreement. This
                  digital submission shall constitute a legally binding
                  acceptance of the Agreement, equivalent to a physical
                  signature. <br></br>
                  <br></br>The Athlete further agrees that their digital
                  acceptance and profile creation is valid and enforceable under
                  applicable electronic transactions and contract laws in
                  Australia.
                </p>

                <p className="text-xs text-gray-500 mt-6">
                  Last updated: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowTermsDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setAcceptedTerms(true);
                  setShowTermsDialog(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-gold border border-transparent rounded-md hover:bg-gold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
              >
                Accept Terms
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailRegister;
